import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

export default function AccountRecovery() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const scheduledAt = location.state?.scheduled_at;
  const scheduledLabel = scheduledAt
    ? new Date(scheduledAt).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
        year: "numeric", month: "long", day: "numeric",
      })
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/account/reactivate", { email, password });
      toast.success(t("app.account.recovery.success"));
      navigate("/login", { replace: true });
    } catch (e2) {
      const code = e2?.response?.data?.detail;
      if (code === "invalid_credentials")
        setError(t("app.account.recovery.invalidCredentials"));
      else if (code === "not_pending_deletion")
        setError(t("app.account.recovery.notPendingDeletion"));
      else setError(extractError(e2, t("app.account.deletionError")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t("app.account.recovery.title")}
      subtitle={t("app.account.recovery.body")}
      testId="account-recovery-page"
    >
      {scheduledLabel ? (
        <div
          className="mb-6 nvl-card p-4 text-sm"
          style={{ borderColor: "hsl(var(--destructive))" }}
          data-testid="account-recovery-scheduled"
        >
          {t("app.account.recovery.scheduledOn", { date: scheduledLabel })}
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-5" data-testid="account-recovery-form">
        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.account.recovery.emailLabel")}
          </label>
          <input
            type="email"
            required
            className="nvl-input mt-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="account-recovery-email"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.account.recovery.passwordLabel")}
          </label>
          <input
            type="password"
            required
            className="nvl-input mt-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="account-recovery-password"
            autoComplete="current-password"
          />
        </div>
        {error ? (
          <div className="text-sm" style={{ color: "hsl(var(--destructive))" }}
               data-testid="account-recovery-error">
            {error}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={loading}
          className="nvl-btn nvl-btn-primary w-full disabled:opacity-60"
          data-testid="account-recovery-submit"
        >
          {loading ? t("common.loading") : t("app.account.recovery.reactivateCta")}
        </button>
        <div className="text-sm text-center" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          <Link to="/login" className="underline">
            {t("common.signIn")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
